import { useContext, useEffect, useMemo, useState } from "react";
import { Button, Divider, Form, Popover, Select } from "antd";
import { message } from "utils/ciq-message";
import dayjs from "utils/dayjs-config";
import {
  createOpcProjectConfig,
  getOpcWorkspaceProjects
} from "services/integration-service";
import WarningIcon from "components/svg-icons/warning-icon";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useWatch } from "antd/lib/form/Form";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import { OPC_INTEGRATION_SYSTEM } from "constants/integrations";
import { getOpcAccounts } from "pages/subscription-settings/integrations-tab-new/utils";
import { IntegrationSystemNameMap } from "../../../../constants";
import { ProjectIntegrationsTabSharedContext } from "../project-integrations-tab-context";

export default function ProjectOpcInitialIntegration(props: any) {
  const { onProjectIntegrated } = props;
  const { sharedState, dispatch } = useContext(
    ProjectIntegrationsTabSharedContext
  );

  const { tokenRetrievalState }: TProjectContext = useContext(ProjectContext);

  const [form] = Form.useForm();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [projects, setProjects] = useState<any>(null);
  const [submittingForm, setSubmittingForm] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);

  const selectedAccountId = sharedState?.additionalData?.opcAccountId;

  const workspaces = useMemo(() => {
    const list: any = [];
    const selectedAccount = selectedAccountId
      ? getOpcAccounts(sharedState.subscriptionIntegrationConfigs).find(
          (account) => account.id === String(selectedAccountId)
        )
      : undefined;
    const opcSubConfigs = selectedAccount
      ? selectedAccount.configs
      : sharedState.subscriptionIntegrationConfigs.filter(
          (subConfig: any) =>
            subConfig.integration.system === OPC_INTEGRATION_SYSTEM
        );
    opcSubConfigs.forEach((opcConfig: any) => {
      list.push({
        name: opcConfig.properties.OPC_WORKSPACE_NAME,
        workspaceId: opcConfig.properties.OPC_WORKSPACE_ID,
        workspaceCode: opcConfig.properties.OPC_WORKSPACE_CODE || "",
        subscriptionId: opcConfig.id,
        properties: opcConfig.properties
      });
    });
    return list;
  }, [sharedState.subscriptionIntegrationConfigs, selectedAccountId]);

  const selectedSubscriptionConfigId = useWatch("workspace_id", form);

  const selectedWorkspace = useMemo(() => {
    return workspaces.find((workspace: any) => {
      return workspace.subscriptionId === selectedSubscriptionConfigId;
    });
  }, [selectedSubscriptionConfigId, workspaces]);

  const selectedWorkspaceProperties = selectedWorkspace?.properties || {};

  useEffect(() => {
    let cancelled = false;

    form.setFieldsValue({ project_id: undefined });
    setProjects(null);
    setLoadingProjects(false);

    if (!selectedSubscriptionConfigId) {
      setProjects([]);
      return undefined;
    }

    if (!tokenRetrievalState.token) {
      setProjects([]);
      message.error("Failed to load OPC projects.");
      return undefined;
    }

    if (!selectedWorkspace) {
      setProjects([]);
      message.error("Failed to load OPC projects.");
      return undefined;
    }

    const loadProjects = async () => {
      setLoadingProjects(true);
      try {
        const projectsResp = await getOpcWorkspaceProjects(
          String(selectedWorkspace.workspaceId),
          tokenRetrievalState?.token
        );
        if (cancelled) return;
        if (projectsResp.success) {
          setProjects(projectsResp.data ?? []);
        } else {
          setProjects([]);
          message.error("Failed to load OPC projects.");
        }
      } catch {
        if (cancelled) return;
        setProjects([]);
        message.error("Failed to load OPC projects.");
      } finally {
        if (!cancelled) {
          setLoadingProjects(false);
        }
      }
    };
    loadProjects();

    return () => {
      cancelled = true;
    };
  }, [
    form,
    selectedSubscriptionConfigId,
    tokenRetrievalState.token,
    selectedWorkspace
  ]);

  const onFinish = (values: any) => {
    setSubmittingForm(true);
    const foundWorkspace = workspaces.find((workspace: any) => {
      return workspace.subscriptionId === values.workspace_id;
    });

    const foundProject = projects.find((project: any) => {
      return project.id === values.project_id;
    });

    const payload = [
      {
        subscription_config_id: foundWorkspace.subscriptionId,
        properties: {
          integration_name: sharedState?.additionalData.newIntegrationName,
          opc_workspace_id: String(foundWorkspace.workspaceId),
          opc_workspace_name: foundWorkspace.name,
          opc_workspace_code: String(foundWorkspace.workspaceCode || ""),
          opc_project_id: values.project_id.toString(),
          opc_project_name: foundProject.name,
          project_alert_email: values.alert_email.join(",")
        }
      }
    ];

    const saveData = async () => {
      const createResponse = await createOpcProjectConfig(
        payload,
        tokenRetrievalState.token
      );

      setSubmittingForm(false);

      if (createResponse.success) {
        message.success("Integration created successfully.");
        onProjectIntegrated(createResponse);
        dispatch({
          type: "SET_ACTIVE_LIST_VIEW"
        });
      } else {
        message.error("Failed to create integration.");
      }
    };
    saveData();
  };

  const confirmPopoverContent = (
    <div>
      <div className="flex justify-center">
        <WarningIcon />
      </div>
      <div className="flex justify-center text-lg font-medium">
        Please confirm your action
      </div>
      <div className="mt-2 mb-3.5">
        Integration details cannot be edited once saved.
      </div>
      <div className="flex items-center justify-center space-x-2">
        <Button
          onClick={() => {
            setShowConfirmDialog(false);
          }}
          disabled={submittingForm}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={() => {
            form.submit();
          }}
          loading={submittingForm}
        >
          Save
        </Button>
      </div>
    </div>
  );

  const workspaceListOptions = useMemo(() => {
    return workspaces
      ?.map((workspace: any) => {
        return {
          label: workspace.name,
          value: workspace.subscriptionId
        };
      })
      .sort((w1: any, w2: any) =>
        w1?.label?.toLowerCase() > w2?.label?.toLowerCase() ? 1 : -1
      );
  }, [workspaces]);

  const projectListOptions = useMemo(() => {
    return projects
      ?.map((project: any) => {
        return {
          label: project.name,
          value: project.id
        };
      })
      .sort((p1: any, p2: any) =>
        p1?.label?.toLowerCase() > p2?.label?.toLowerCase() ? 1 : -1
      );
  }, [projects]);

  const lastAuthorized =
    selectedWorkspaceProperties.LAST_AUTHORIZED ||
    selectedWorkspaceProperties.LAST_UPDATED;
  const opcAuthorizerName =
    selectedWorkspaceProperties.AUTHORIZER_OPC_NAME || "";
  const opcAuthorizerEmail =
    selectedWorkspaceProperties.AUTHORIZER_OPC_EMAIL || "";
  const ciqAuthorizerName =
    selectedWorkspaceProperties.AUTHORIZER_CIQ_NAME ||
    selectedWorkspaceProperties.SUBSCRIPTION_AUTHORIZER_CIQ_NAME ||
    "";
  const ciqAuthorizerEmail =
    selectedWorkspaceProperties.AUTHORIZER_CIQ_EMAIL ||
    selectedWorkspaceProperties.SUBSCRIPTION_AUTHORIZER_CIQ_EMAIL ||
    "";

  return (
    <Form
      form={form}
      onFinish={onFinish}
      layout="vertical"
      className="h-full flex flex-col"
      disabled={submittingForm}
    >
      <div className="h-full relative">
        <div className="grow w-full md:max-w-screen-sm mx-auto py-4">
          <div className="mt-5 ">
            <div className="text-sm uppercase font-semibold text-neutral-700">
              You are authorized with{" "}
              {IntegrationSystemNameMap[sharedState?.system]}
            </div>
            <div className="text-xs mt-1">
              <span className="text-neutral-600">Last authorized on: </span>
              <span>
                {lastAuthorized
                  ? dayjs(lastAuthorized).local().format("hh:mmA MM/DD/YYYY")
                  : null}
              </span>
            </div>
          </div>
          <div className="mt-7 w-[500px]">
            <Form.Item
              name="workspace_id"
              className="hide-required-mark"
              label={
                <div className="flex items-center">
                  <span>Workspace Name</span>
                </div>
              }
              rules={[{ required: true, message: "Please select a Workspace" }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={workspaceListOptions}
                placeholder="Select a Workspace"
                notFoundContent="No workspaces available"
                className="normal-case"
                loading={!workspaceListOptions}
                allowClear={false}
              />
            </Form.Item>
            <Form.Item
              name="project_id"
              className="hide-required-mark"
              label={
                <div className="flex items-center">
                  <span>Project Name</span>
                </div>
              }
              rules={[{ required: true, message: "Please select a Project" }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={projectListOptions}
                placeholder="Select a Project"
                className="normal-case"
                loading={
                  !!selectedSubscriptionConfigId &&
                  (loadingProjects || !projectListOptions)
                }
                allowClear={false}
                disabled={
                  !selectedSubscriptionConfigId ||
                  loadingProjects ||
                  !projectListOptions
                }
              />
            </Form.Item>
            <Form.Item
              name="alert_email"
              className="hide-required-mark"
              label={
                <div className="flex items-center">
                  <span>Notification Recipients</span>
                  <Popover
                    content={
                      <div className="text-sm font-medium">
                        Enter the email address of users who need to be notified
                        in case of failure in authorization.
                      </div>
                    }
                  >
                    <InfoCircleOutlined className="pl-1 -mt-0.5" />
                  </Popover>
                </div>
              }
              rules={[
                { required: true, message: "Please enter at least one email" }
              ]}
            >
              <Select
                className="normal-case"
                mode="tags"
                placeholder="Email IDs"
                notFoundContent={null}
                styles={{ popup: { root: { display: "none" } } }}
              />
            </Form.Item>
          </div>
          <div className="mt-9 grid grid-cols-2 gap-x-4">
            <div className="space-y-1">
              <div className="info-label">
                AUTHORIZATION GRANTED BY (OPC USER):
              </div>
              <div className="info-value">
                {opcAuthorizerName || opcAuthorizerEmail || "—"}
                {opcAuthorizerName && opcAuthorizerEmail && (
                  <> ({opcAuthorizerEmail})</>
                )}
              </div>
            </div>
            <div className="space-y-1">
              <div className="info-label">AUTHORIZATION INITIATED BY:</div>
              <div className="info-value">
                {ciqAuthorizerName || ciqAuthorizerEmail
                  ? `${ciqAuthorizerName}${
                      ciqAuthorizerEmail ? ` (${ciqAuthorizerEmail})` : ""
                    }`
                  : "—"}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Divider className="mx-0 pb-0 mb-0" />
      <footer className="p-6 flex justify-end gap-x-4">
        <Button
          size="middle"
          onClick={() => {
            dispatch({
              type: "SET_ACTIVE_LIST_VIEW"
            });
          }}
          disabled={submittingForm}
        >
          Back
        </Button>
        <Popover
          placement="topRight"
          content={confirmPopoverContent}
          destroyOnHidden
          open={showConfirmDialog}
        >
          <Button
            size="middle"
            type="primary"
            onClick={async () => {
              try {
                await form.validateFields();
                setShowConfirmDialog(true);
              } catch (ex) {
                console.error(ex);
              }
            }}
            disabled={submittingForm}
          >
            Save
          </Button>
        </Popover>
      </footer>
    </Form>
  );
}
